import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'next-i18next';
import { capitalize, formatDate } from '@/utils/helpers';
import { useCountries } from '@/services/countries';
import { useWizard } from '@/modules/wizard';
import { Asterisk, Button, PhoneInput, Select, Textfield } from '@/components/atoms';
import type { OptionType } from '@/components/atoms/Select';
import { PassengerFormType, PassengersFormProps } from './PassengersForm.types';
import { CustomForm, Row } from './PassengersForm.styles';
import { AirTraveler, IdDocumentTypeEnum, KiuPassengerFormMutationVariables, NamePrefixEnum, PassengerGender, PassengerTypesEnum } from '@/graphql/types';
import * as FeatherIcon from 'react-feather';
import { Datepicker } from '@/components/molecules';
import { useKIUPassengerForm } from '@/services/flight-booking';
import { showErrorAlert } from '@/modules/alerts';

const PassengersForm: React.FC<PassengersFormProps> = ({ passenger, initialData, position, isTheFirst, isTheLast }) => {
    const { t } = useTranslation(); // Translations
    const { state, dispatch } = useWizard();
    const { countries } = useCountries();
    const { passengerForm, loading } = useKIUPassengerForm();

    // Forms initial values
    const initialValues: PassengerFormType = {
        gender: initialData?.gender || '',
        title: initialData?.namePrefix || '',
        firstName: initialData?.givenName || '',
        lastName: initialData?.surname || '',
        idDocumentType: initialData?.idDocumentType || 'PASSPORT',
        idNumber: initialData?.idNumber || '',
        birthCountry: initialData?.birthCountry || '',
        passportIssueCountry: initialData?.documentIssueCountry || '',
        birthDate: initialData?.birthdate ? new Date(initialData?.birthdate as string) : '',
        passportExpireDate: initialData?.documentExpireDate ? new Date(initialData?.documentExpireDate as string) : '',
        passportCreatedDate: initialData?.passportCreatedDate ? new Date(initialData?.passportCreatedDate as string) : '',
        countryAccessCode: initialData?.countryAccessCode || 'us',
        phoneAreaCityCode: initialData?.phoneAreaCityCode || 'us',
        phoneNumber: initialData?.phoneNumber || '',
        email: initialData?.email || '',
    };

    const titleOptions = [
        { value: 'MR', label: t('flights:passenger.title.mr') },
        { value: 'MRS', label: t('flights:passenger.title.mrs') },
        { value: 'MSS', label: t('flights:passenger.title.ms') },
        { value: 'JR', label: t('flights:passenger.title.jr') },
    ];

    const genderOptions = [
        { value: PassengerGender.Male, label: t('flights:label.male') },
        { value: PassengerGender.Female, label: t('flights:label.female') },
    ];

    const documentTypeOptions = [{ value: 'PASSPORT', label: t('flights:passenger.id_document_type.passport') }];

    const countryOptions = countries?.map((count: { id: string; name: string }) => ({
        value: count.id,
        label: capitalize(count.name),
    }));

    const validationSchema = Yup.object({
        gender: Yup.mixed().required(t('flights:label.required')),
        title: Yup.mixed().required(t('flights:label.required')),
        firstName: Yup.string().required(t('flights:label.required')),
        lastName: Yup.string().required(t('flights:label.required')),
        idDocumentType: Yup.mixed().required(t('flights:label.required')),
        idNumber: Yup.string().required(t('flights:label.required')),
        passportIssueCountry: Yup.mixed().required(t('flights:label.required')),
        birthCountry: Yup.mixed().required(t('flights:label.required')),
        birthDate: Yup.string().required(t('flights:label.required')),
        passportExpireDate: Yup.string().required(t('flights:label.required')),
        passportCreatedDate: Yup.string().required(t('flights:label.required')),
        countryAccessCode: Yup.mixed().required(t('flights:label.required')),
        phoneAreaCityCode: Yup.string().required(t('flights:label.required')),
        phoneNumber: Yup.string().required(t('flights:label.required')),
        email: Yup.string().required(t('flights:label.required')),
    });

    const handleSubmit = async (values: PassengerFormType) => {
        const airTraveler = {
            passengerType: passenger.type as PassengerTypesEnum,
            namePrefix: values.title as unknown as NamePrefixEnum,
            givenName: values.firstName as string,
            surname: values.lastName as string,
            idNumber: values.idNumber as string,
            idDocumentType: values.idDocumentType as unknown as IdDocumentTypeEnum,
            birthCountry: values.birthCountry as unknown as string,
            documentIssueCountry: values.passportIssueCountry as unknown as string,
            travelerRefNumber: passenger.number,
            email: values.email as unknown as string,
            gender: values.gender as string,
            birthdate: formatDate(values.birthDate as unknown as string),
            documentExpireDate: formatDate(values.passportExpireDate as unknown as string),
            passportCreatedDate: formatDate(values.passportCreatedDate as unknown as string),
            phoneNumber: values.phoneNumber as unknown as string,
            phoneAreaCityCode: values.phoneAreaCityCode as unknown as string,
            countryAccessCode: values.countryAccessCode as string,
            id: state.data?.travelers ? state.data?.travelers[position]?.id : null,
        };

        const { id, ...airTravelerData } = airTraveler;

        const queryData: KiuPassengerFormMutationVariables = {
            input: {
                passenger: {
                    ...airTravelerData,
                    middleName: '',
                    passengerType: airTraveler.passengerType === PassengerTypesEnum.Adult ? PassengerTypesEnum.Adult : PassengerTypesEnum.Child,
                },
                id: id || '',
            },
        };

        await passengerForm({
            variables: queryData,
            onCompleted: async (data) => {
                if (data?.kiuPassengerForm?.status === 200) {
                    const newTravelersArray = [...(state.data?.travelers || [])];
                    const newLuggageArray = [...(state.data?.luggage || [])];
                    airTraveler.id = data?.kiuPassengerForm?.response;
                    if (position >= 0 && position < newTravelersArray.length) {
                        newTravelersArray[position] = airTraveler as AirTraveler & { id: string };
                        newLuggageArray[position] = 1;
                    } else {
                        newTravelersArray.push(airTraveler as AirTraveler & { id: string });
                        newLuggageArray.push(1);
                    }

                    dispatch({
                        type: 'UPDATE_DATA',
                        payload: {
                            passengerActive: !isTheLast ? position + 1 : position,
                            travelers: newTravelersArray as [AirTraveler],
                            luggage: newLuggageArray as [number],
                        },
                    });

                    dispatch({
                        type: 'UPDATE_DATA',
                        payload: {
                            allowReserve: isTheLast,
                        },
                    });
                } else {
                    await showErrorAlert({
                        title: t('itinerary:error.passenger-form.title'),
                        text: t('itinerary:error.passenger-form.description'),
                        confirmButtonText: t('itinerary:button.confirm'),
                        onConfirm: () => {},
                    });
                }
            },
            onError: async () => {
                await showErrorAlert({
                    title: t('itinerary:error.passenger-form.title'),
                    text: t('itinerary:error.passenger-form.description'),
                    confirmButtonText: t('itinerary:button.confirm'),
                    onConfirm: () => {},
                });
            },
        });
    };

    return (
        <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ errors, values, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }) => {
                const selectedTitleOption = titleOptions.find((option) => option.value === values.title);
                const selectedGenderOption = genderOptions.find((option) => option.value === values.gender);
                const selectedIdDocumentTypeOption = documentTypeOptions.find((option) => option.value === values.idDocumentType);
                const selectedNationalityOption = countryOptions?.find((option) => option.value === values.birthCountry);
                const selectedPassportIssueCountryOption = countryOptions?.find((option) => option.value === values.passportIssueCountry);

                return (
                    <CustomForm>
                        <Row style={{ marginTop: '28px' }}>
                            <Select
                                id="gender"
                                key="gender"
                                label={<Asterisk text={t('flights:passenger.gender.label')} />}
                                placeholder={t('flights:passenger.gender.placeholder')}
                                defaultValue={selectedGenderOption}
                                options={genderOptions}
                                onChange={(option) => {
                                    const optionValue = option as OptionType;
                                    void setFieldValue('gender', optionValue?.value || '');
                                }}
                                onBlur={() => void setFieldTouched('gender', true)}
                                errors={touched.gender && errors.gender}
                                success={(touched.gender || values?.gender !== '') && !errors.gender}
                            />
                            <Select
                                id="title"
                                key="title"
                                label={<Asterisk text={t('flights:passenger.title.label')} />}
                                placeholder={t('flights:passenger.title.placeholder')}
                                defaultValue={selectedTitleOption}
                                options={titleOptions}
                                onChange={(option) => {
                                    const optionValue = option as OptionType;
                                    void setFieldValue('title', optionValue?.value || '');
                                }}
                                onBlur={() => void setFieldTouched('title', true)}
                                errors={touched.title && errors.title}
                                success={(touched.title || values?.title !== '') && !errors.title}
                            />
                        </Row>
                        <Row style={{ marginTop: '28px' }}>
                            <Textfield
                                id="firstName"
                                type="text"
                                label={<Asterisk text={t('flights:passenger.firstname.label')} />}
                                placeholder={t('flights:passenger.firstname.placeholder')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.firstName}
                                errors={touched.firstName && errors.firstName}
                                success={(touched.firstName || values?.firstName !== '') && !errors.firstName}
                            />
                            <Textfield
                                id="lastName"
                                type="text"
                                label={<Asterisk text={t('flights:passenger.lastname.label')} />}
                                placeholder={t('flights:passenger.lastname.placeholder')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.lastName}
                                errors={touched.lastName && errors.lastName}
                                success={(touched.lastName || values?.lastName !== '') && !errors.lastName}
                            />
                        </Row>
                        <Row>
                            <Textfield
                                id="idNumber"
                                type="text"
                                label={<Asterisk text={t('flights:passenger.id_number.label')} />}
                                placeholder={t('flights:passenger.id_number.placeholder')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.idNumber}
                                errors={touched.idNumber && errors.idNumber}
                                success={(touched.idNumber || values?.idNumber !== '') && !errors.idNumber}
                            />
                            <Datepicker
                                dateFormat="yyyy-MM-dd"
                                id="birthDate"
                                label={<Asterisk text={t('flights:passenger.birth-date.label')} />}
                                placeholder={t('flights:passenger.birth-date.placeholder')}
                                selected={values?.birthDate}
                                onChange={(birthDate) => void setFieldValue('birthDate', birthDate)}
                                maxDate={new Date()}
                                leftIcon={<FeatherIcon.Calendar />}
                                onBlur={handleBlur}
                                errors={touched.birthDate && errors.birthDate}
                                success={(touched.birthDate || !!values?.birthDate) && !errors.birthDate}
                            />
                        </Row>
                        <Row>
                            <Select
                                id="birthCountry"
                                key="birthCountry"
                                label={<Asterisk text={t('flights:passenger.nationality.label')} />}
                                placeholder={t('flights:passenger.nationality.placeholder')}
                                defaultValue={selectedNationalityOption}
                                options={countryOptions}
                                onChange={(option) => {
                                    const optionValue = option as OptionType;
                                    void setFieldValue('birthCountry', optionValue?.value || '');
                                }}
                                onBlur={() => void setFieldTouched('birthCountry', true)}
                                errors={touched.birthCountry && errors.birthCountry}
                                success={(touched.birthCountry || values?.birthCountry !== '') && !errors.birthCountry}
                            />
                            <Select
                                id="passportIssueCountry"
                                key="passportIssueCountry"
                                label={<Asterisk text={t('flights:passenger.passport-issue-country.label')} />}
                                placeholder={t('flights:passenger.passport-issue-country.placeholder')}
                                defaultValue={selectedPassportIssueCountryOption}
                                options={countryOptions}
                                onChange={(option) => {
                                    const optionValue = option as OptionType;
                                    void setFieldValue('passportIssueCountry', optionValue?.value || '');
                                }}
                                onBlur={() => void setFieldTouched('passportIssueCountry', true)}
                                errors={touched.passportIssueCountry && errors.passportIssueCountry}
                                success={(touched.passportIssueCountry || values?.passportIssueCountry !== '') && !errors.passportIssueCountry}
                            />
                        </Row>
                        <Row style={{ marginTop: '28px' }}>
                            <Datepicker
                                dateFormat="yyyy-MM-dd"
                                id="passportCreatedDate"
                                label={<Asterisk text={t('flights:passenger.passport-created-date.label')} />}
                                placeholder={t('flights:passenger.passport-created-date.placeholder')}
                                selected={values?.passportCreatedDate}
                                onChange={(passportCreatedDate) => void setFieldValue('passportCreatedDate', passportCreatedDate)}
                                maxDate={new Date()}
                                leftIcon={<FeatherIcon.Calendar />}
                                onBlur={handleBlur}
                                errors={touched.passportCreatedDate && errors.passportCreatedDate}
                                success={(touched.passportCreatedDate || !!values?.passportCreatedDate) && !errors.passportCreatedDate}
                            />
                            <Datepicker
                                dateFormat="yyyy-MM-dd"
                                id="passportExpireDate"
                                label={<Asterisk text={t('flights:passenger.passport-expire-date.label')} />}
                                placeholder={t('flights:passenger.passport-expire-date.placeholder')}
                                selected={values?.passportExpireDate}
                                onChange={(passportExpireDate) => void setFieldValue('passportExpireDate', passportExpireDate)}
                                minDate={new Date()}
                                leftIcon={<FeatherIcon.Calendar />}
                                onBlur={handleBlur}
                                errors={touched.passportExpireDate && errors.passportExpireDate}
                                success={(touched.passportExpireDate || !!values?.passportExpireDate) && !errors.passportExpireDate}
                            />
                        </Row>

                        <Row>
                            <PhoneInput
                                id="phoneNumber"
                                key="phoneNumber"
                                label={<Asterisk text={t('flights:passenger.phone.label')} />}
                                placeholder={t('flights:passenger.phone.placeholder')}
                                searchPlaceholder={t('flights:passenger.phone.search')}
                                searchNotFound={t('flights:passenger.phone.search-not-found')}
                                country={values.phoneAreaCityCode}
                                preferredCountries={['jm', 'us', 'cu']}
                                defaultValue={values?.phoneNumber}
                                onChange={(value, data) => {
                                    void setFieldValue('countryAccessCode', data.dialCode);
                                    void setFieldValue('phoneAreaCityCode', data.dialCode);
                                    void setFieldValue('phoneNumber', value);
                                }}
                                onBlur={() => {
                                    void setFieldTouched('countryAccessCode', true);
                                    void setFieldTouched('phoneAreaCityCode', true);
                                    void setFieldTouched('phoneNumber', true);
                                }}
                                errors={touched.phoneNumber && errors.phoneNumber}
                                success={(touched.phoneNumber || values?.phoneNumber !== '') && !errors.phoneNumber}
                            />
                            <Textfield
                                id="email"
                                type="text"
                                label={<Asterisk text={t('flights:passenger.email.label')} />}
                                placeholder={t('flights:passenger.email.placeholder')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.email}
                                errors={touched.email && errors.email}
                                success={(touched.email || values?.email !== '') && !errors.email}
                            />
                        </Row>

                        <Row
                            style={{
                                marginTop: isTheFirst ? '28px' : '',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button content={t('flights:passenger.button.save-continue')} type="submit" variant="filled" isSubmitting={loading} />
                        </Row>
                    </CustomForm>
                );
            }}
        </Formik>
    );
};

export default PassengersForm;
